import type { Suggestion } from '@brain/contracts'
import { Button, Card, Chip, EmptyState, Segmented, toast } from '@brain/ui'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { CalendarClock, FileText, Radio, ScrollText, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CopilotHistory } from '../../components/live/CopilotHistory.tsx'
import { MdView } from '../../components/MdView.tsx'
import { formatDateTime, MeetingStatusChip } from '../../components/meetings/MeetingList.tsx'
import { MeetingNotesEditor } from '../../components/meetings/MeetingNotesEditor.tsx'
import { MeetingRecording } from '../../components/meetings/MeetingRecording.tsx'
import { MeetingScreenshots } from '../../components/meetings/MeetingScreenshots.tsx'
import { MeetingSpeakers } from '../../components/meetings/MeetingSpeakers.tsx'
import { MeetingTimeEntry } from '../../components/meetings/MeetingTimeEntry.tsx'
import { SuggestionCard } from '../../components/meetings/SuggestionCard.tsx'
import { TranscriptView } from '../../components/meetings/TranscriptView.tsx'
import { NoteTasks } from '../../components/note/NoteTasks.tsx'
import { MeetingPrepSection } from '../../components/people/MeetingPrepSection.tsx'
import { primeAudio } from '../../lib/live/audio.ts'
import {
  meetingQuery,
  transcriptQuery,
  useAcceptAll,
  useDeleteTranscript,
  useTriageV2,
} from '../../lib/m2.ts'
import { buildSuggestionPatch, type SuggestionDraft } from '../../lib/triage-patch.ts'

type MeetingTab = 'overview' | 'transcript' | 'tips'
const TABS: MeetingTab[] = ['overview', 'transcript', 'tips']

export const Route = createFileRoute('/_app/meetings/$meetingId')({
  // ?t=<ms> jumps to a transcript position (links from the meeting note) and opens the transcript tab
  validateSearch: (s: Record<string, unknown>): { t?: number; tab?: MeetingTab } => {
    const n = Number(s.t)
    const tab = TABS.find((x) => x === s.tab)
    return {
      ...(Number.isFinite(n) && n >= 0 && s.t !== undefined && s.t !== '' ? { t: n } : {}),
      ...(tab ? { tab } : {}),
    }
  },
  component: MeetingPage,
})

function MeetingPage() {
  const { t } = useTranslation()
  const { meetingId } = Route.useParams()
  const search = Route.useSearch()
  const navigate = useNavigate()
  const { me } = Route.useRouteContext()
  const q = useQuery(meetingQuery(meetingId))
  const m = q.data
  const transcript = useQuery({ ...transcriptQuery(meetingId), enabled: Boolean(m?.hasTranscript) })
  const triage = useTriageV2()
  const acceptAll = useAcceptAll()
  const delTranscript = useDeleteTranscript(meetingId)
  const [drafts, setDrafts] = useState<Record<string, SuggestionDraft>>({})
  const [confirm, setConfirm] = useState(false)
  const tab: MeetingTab = search.tab ?? (search.t !== undefined ? 'transcript' : 'overview')
  const setTab = (x: MeetingTab) =>
    void navigate({
      to: '/meetings/$meetingId',
      params: { meetingId },
      search: x === 'overview' ? {} : { tab: x },
      replace: true,
    })

  if (q.isError)
    return (
      <div className="flex flex-1 items-center justify-center">
        <EmptyState title={t('meetings.notFound')} description={t('meetings.notFoundText')} />
      </div>
    )
  if (!m) return <p className="p-8 text-[13px] text-meta">{t('common.loading')}</p>

  // 'live' source/status (ADR 0006) are compared as strings until the M2 contract enums carry them
  const isLiveSource = (m.source as string) === 'live'
  const isRunning = (m.status as string) === 'live'
  const pending = m.suggestions.filter((s) => s.status === 'pending' && !s.autoAccepted)
  const act = (s: Suggestion, action: 'accept' | 'discard') =>
    triage.mutate(
      {
        action,
        items: [
          {
            kind: 'suggestion',
            id: s.id,
            ...(action === 'accept' ? { patch: buildSuggestionPatch(s, drafts[s.id] ?? {}) } : {}),
          },
        ],
      },
      { onError: () => toast(t('common.error'), { tone: 'attention' }) },
    )

  return (
    <div className="min-w-0 flex-1 overflow-y-auto">
      <div className="mx-auto flex max-w-3xl flex-col gap-5 px-4 py-4 md:px-8 md:py-6">
        <header data-tauri-drag-region className="flex flex-col gap-2">
          <Link to="/meetings" className="text-[13px] text-meta hover:text-text-1">
            ← {t('nav.meetings')}
          </Link>
          <div className="flex items-start gap-3">
            <span className="mt-1 flex size-9 shrink-0 items-center justify-center rounded-tile bg-sky-bg text-sky-fg">
              <CalendarClock size={18} strokeWidth={1.9} />
            </span>
            <div className="min-w-0 flex-1">
              <h1 className="text-[24px] text-text-1 leading-tight">{m.title}</h1>
              <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[13px] text-text-2">
                <span>{formatDateTime(m.startedAt)}</span>
                {m.containerId && m.containerTitle && (
                  <Link to="/c/$containerId" params={{ containerId: m.containerId }}>
                    <Chip>{m.containerTitle}</Chip>
                  </Link>
                )}
                <Chip tone="sky">{t(`meetings.source.${m.source}`)}</Chip>
                <MeetingStatusChip m={m} />
              </div>
            </div>
          </div>
          {isRunning && (
            <Card className="flex items-center gap-3 border-pop bg-pop-tint px-4 py-3">
              <Radio size={16} className="shrink-0 text-pop-fg" />
              <p className="min-w-0 flex-1 text-[13px] text-text-1">
                {t('live.resumeBanner', { title: m.title })}
              </p>
              <Link onClick={primeAudio} to="/meetings/live/$meetingId" params={{ meetingId: m.id }}>
                <Button size={32} tabIndex={-1}>
                  {t('meetings.resume')}
                </Button>
              </Link>
            </Card>
          )}
          {m.status === 'failed' && m.error && (
            <Card className="border-apricot bg-apricot-bg px-4 py-3 text-[13px] text-apricot-fg">
              {m.error}
            </Card>
          )}
          {m.participants.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {m.participants.map((p) => (
                <Chip key={`${p.name}-${p.email ?? ''}`} title={p.email ?? undefined}>
                  {p.name}
                </Chip>
              ))}
            </div>
          )}
          <MeetingTimeEntry meetingId={m.id} processed={m.status === 'processed'} />
          <Segmented
            value={tab}
            onValueChange={setTab}
            aria-label={t('meetings.tabs.overview')}
            options={[
              { value: 'overview', label: t('meetings.tabs.overview') },
              { value: 'transcript', label: t('meetings.tabs.transcript') },
              ...(isLiveSource ? [{ value: 'tips' as const, label: t('meetings.tabs.tips') }] : []),
            ]}
            className="self-start"
          />
        </header>

        {tab === 'tips' && isLiveSource ? (
          <CopilotHistory meetingId={m.id} />
        ) : tab === 'overview' ? (
          <>
            <Card className="p-5">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <h2 className="flex-1 meta-label">{t('meetings.summary')}</h2>
                {m.hasTranscript && (
                  <button
                    type="button"
                    onClick={() => setTab('transcript')}
                    className="flex items-center gap-1 font-medium text-[13px] text-pop-fg"
                  >
                    <ScrollText size={14} />
                    {t('meetings.openTranscript')}
                  </button>
                )}
                {m.noteId && (
                  <Link
                    to="/n/$noteId"
                    params={{ noteId: m.noteId }}
                    className="flex items-center gap-1 font-medium text-[13px] text-pop-fg"
                  >
                    <FileText size={14} />
                    {t('meetings.openNote')}
                  </Link>
                )}
              </div>
              {m.summaryMd ? (
                <MdView md={m.summaryMd} className="mt-3" />
              ) : (
                <p className="mt-3 text-[13px] text-text-2">{t('meetings.noSummary')}</p>
              )}
            </Card>

            <Card className="flex flex-col gap-3 p-5">
              <h2 className="meta-label">{t('meetings.notesEditor.title')}</h2>
              <MeetingNotesEditor
                meeting={m}
                user={{ id: me.id, name: me.name }}
                autoCreate={isLiveSource || Boolean(m.userNotes?.trim())}
              />
            </Card>

            <MeetingScreenshots
              meetingId={m.id}
              canReprocess={m.status === 'processed' || m.status === 'failed'}
            />

            {m.noteId && <NoteTasks noteId={m.noteId} />}

            <MeetingPrepSection meetingId={m.id} startedAt={m.startedAt} />

            <section className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <h2 className="flex-1 meta-label">{t('meetings.suggestions')}</h2>
                {pending.length > 1 && (
                  <Button
                    size={32}
                    variant="secondary"
                    onClick={() =>
                      acceptAll.mutate(m.id, {
                        onError: () => toast(t('common.error'), { tone: 'attention' }),
                      })
                    }
                  >
                    {t('suggestion.acceptAll', { count: pending.length })}
                  </Button>
                )}
              </div>
              {m.suggestions.length === 0 && (
                <p className="text-[13px] text-meta">{t('meetings.noSuggestions')}</p>
              )}
              {m.suggestions.map((s) => (
                <SuggestionCard
                  key={s.id}
                  s={s}
                  draft={drafts[s.id] ?? {}}
                  onDraft={(d) => setDrafts((x) => ({ ...x, [s.id]: d }))}
                  onAccept={() => act(s, 'accept')}
                  onDiscard={() => act(s, 'discard')}
                />
              ))}
            </section>
          </>
        ) : (
          <section className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <h2 className="flex-1 meta-label">{t('meetings.transcript')}</h2>
              {m.hasTranscript && !confirm && (
                <Button size={32} variant="ghost" onClick={() => setConfirm(true)}>
                  <Trash2 size={14} />
                  {t('meetings.deleteTranscript')}
                </Button>
              )}
            </div>
            {confirm && (
              <Card className="flex flex-wrap items-center gap-3 border-apricot bg-apricot-bg px-4 py-3">
                <p className="min-w-0 flex-1 text-[13px] text-apricot-fg">
                  {t('meetings.deleteTranscriptConfirm')}
                </p>
                <Button size={32} variant="ghost" onClick={() => setConfirm(false)}>
                  {t('common.cancel')}
                </Button>
                <Button
                  size={32}
                  variant="danger"
                  onClick={() =>
                    delTranscript.mutate(undefined, {
                      onSuccess: () => {
                        setConfirm(false)
                        toast(t('meetings.transcriptDeleted'))
                      },
                      onError: () => toast(t('common.error'), { tone: 'attention' }),
                    })
                  }
                >
                  {t('meetings.deleteTranscript')}
                </Button>
              </Card>
            )}
            <MeetingRecording meetingId={m.id} seekMs={search.t} />
            {!m.hasTranscript ? (
              <p className="text-[13px] text-meta">{t('meetings.noTranscript')}</p>
            ) : transcript.isPending ? (
              <p className="text-[13px] text-meta">{t('common.loading')}</p>
            ) : (
              <Card className="flex flex-col gap-3 p-3">
                {!isRunning && (
                  <MeetingSpeakers
                    meetingId={m.id}
                    segments={transcript.data?.segments ?? []}
                    names={m.speakerNames ?? {}}
                    containerId={m.containerId}
                    canReprocess={m.status === 'processed' || m.status === 'failed'}
                  />
                )}
                <TranscriptView
                  segments={transcript.data?.segments ?? []}
                  anchorMs={search.t}
                  maxHeight="70vh"
                  onSelect={(ms) =>
                    void navigate({
                      to: '/meetings/$meetingId',
                      params: { meetingId },
                      search: { t: ms },
                      replace: true,
                    })
                  }
                />
              </Card>
            )}
          </section>
        )}
      </div>
    </div>
  )
}
